import {
  Close,
  Mic,
  VolumeUp,
  PlayArrow,
  Pause,
  Chat,
  RecordVoiceOver,
} from "@mui/icons-material";
import SendIcon from "@mui/icons-material/Send";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  styled,
  TextField,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
// import { webChatSocket } from "../../socket";
import { useSnackbar } from "notistack";
import { TypeAnimation } from "react-type-animation";
import { webChatSocket, webRealtimeSocket } from "../../socket";

const PREFIX = "AIDialog";

const classes = {
  messagesContainer: `${PREFIX}-messagesContainer`,
  AIResponseContainer: `${PREFIX}-AIResponseContainer`,
  userTextContainer: `${PREFIX}-userTextContainer`,
};

const AIDialogContainer = styled("div")(({ theme }) => ({
  [`& .${classes.messagesContainer}`]: {
    height: "60vh",
    width: "70vw",
    maxWidth: "350px",
  },

  [`& .${classes.AIResponseContainer}`]: {
    height: "79%",
    overflowY: "auto",
    padding: "0.5rem",
    "&::-webkit-scrollbar": {
      width: "5px",
      height: "5px",
    },
    "&::-webkit-scrollbar-thumb": {
      backgroundColor: theme.palette.primary.main,
      borderRadius: "3px",
    },
    "&::-webkit-scrollbar-track": {
      backgroundColor: "rgb(181 154 79 / 58%)",
      borderRadius: "10px",
    },
  },

  [`& .${classes.userTextContainer}`]: {
    marginTop: "1rem",
    minHeight: "100px",
    display: "flex",
    alignItems: "flex-end",
    gap: theme.spacing(1),
  },
}));

const AIDialog = ({
  open,
  onClose,
  configId,
  targetId,
  zoneId,
  introductionAI,
  speechToSpeech,
}) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [message, setMessage] = useState("");
  const [answers, setAnswers] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [playingIndex, setPlayingIndex] = useState(null);
  const [inputMode, setInputMode] = useState(null); // null, "write" ou "talk"
  const [modeSelected, setModeSelected] = useState(false);
  const [isResponseCooldown, setIsResponseCooldown] = useState(false); // Cooldown pour éviter "conversation_already_has_active_response"
  const [expandedTranscriptions, setExpandedTranscriptions] = useState(
    new Set()
  ); // Pour tracker les transcriptions étendues
  const [isAudioPaused, setIsAudioPaused] = useState(false); // Contrôle pause/resume de l'audio

  const tmpAnswers = useRef([]);
  const mediaRecorderRef = useRef(null);
  const audioContextRef = useRef(null);
  const audioBufferRef = useRef([]);
  const currentAudioRef = useRef(null);
  const isReceivingAudioRef = useRef(false);
  const recordedAudioBlobRef = useRef(null);
  const audioSavedRef = useRef(false); // Pour éviter de sauvegarder l'audio plusieurs fois
  const recordingStartTimeRef = useRef(null); // Pour tracker le temps d'enregistrement
  const cooldownTimerRef = useRef(null); // Timer pour le cooldown
  const shouldSendChunksRef = useRef(false); // Flag pour contrôler l'envoi des chunks
  const pendingTranscriptionRef = useRef(null); // Pour stocker temporairement la transcription utilisateur
  const pendingAITranscriptionRef = useRef(null); // Pour stocker temporairement la transcription IA
  const microphonePermissionGrantedRef = useRef(false); // Pour tracker si l'autorisation a été accordée

  // Refs pour le streaming audio en temps réel
  const playbackAudioContextRef = useRef(null); // AudioContext pour la lecture en streaming
  const playbackSourceNodeRef = useRef(null); // SourceNode actuel
  const playbackStartTimeRef = useRef(0); // Temps de début de lecture
  const playbackScheduledTimeRef = useRef(0); // Temps déjà schedulé
  const streamingBufferRef = useRef([]); // Buffer pour les chunks en attente
  const isStreamingAudioRef = useRef(false); // Flag pour savoir si on est en train de streamer
  const streamingTimeoutRef = useRef(null); // Timeout pour détecter les problèmes de connexion
  const totalChunksToPlayRef = useRef(0); // Nombre total de chunks à jouer
  const chunksPlayedRef = useRef(0); // Nombre de chunks déjà joués

  const SAMPLE_RATE = 24000;
  const MIN_RECORDING_DURATION = 600; // Durée minimale en millisecondes (600 ms)
  const RESPONSE_COOLDOWN = 1000; // Délai de sécurité après la fin d'une réponse (1 seconde)
  const MAX_TRANSCRIPTION_LENGTH = 100; // Longueur maximale avant "afficher plus"

  // Constantes pour le streaming audio
  const MIN_BUFFER_CHUNKS = 10; // Nombre minimum de chunks avant de commencer à jouer (buffering)
  const STREAMING_TIMEOUT = 2000; // Timeout en ms pour détecter une connexion lente (2 secondes)

  // Fonctions utilitaires pour les transcriptions
  const truncateTranscription = (
    text,
    maxLength = MAX_TRANSCRIPTION_LENGTH
  ) => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + "...";
  };

  const toggleTranscriptionExpansion = (messageIndex) => {
    const newExpanded = new Set(expandedTranscriptions);
    if (newExpanded.has(messageIndex)) {
      newExpanded.delete(messageIndex);
    } else {
      newExpanded.add(messageIndex);
    }
    setExpandedTranscriptions(newExpanded);
  };

  // Utility functions for audio processing
  const base64ToUint8Array = (base64) => {
    const binaryString = atob(base64);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    return bytes;
  };

  const uint8ArrayToBase64 = (bytes) => {
    let binary = "";
    const chunkSize = 0x8000; // 32768 bytes - traitement par chunks pour éviter stack overflow

    for (let i = 0; i < bytes.length; i += chunkSize) {
      const chunk = bytes.subarray(i, Math.min(i + chunkSize, bytes.length));
      binary += String.fromCharCode.apply(null, Array.from(chunk));
    }

    return btoa(binary);
  };

  const amplifyAudio = (pcmData, amplificationFactor = 2.5) => {
    const amplified = new Uint8Array(pcmData.length);
    for (let i = 0; i < pcmData.length - 1; i += 2) {
      let sample = pcmData[i] | (pcmData[i + 1] << 8);
      if (sample > 32767) sample -= 65536;
      sample = Math.round(sample * amplificationFactor);
      sample = Math.max(-32768, Math.min(32767, sample));
      if (sample < 0) sample += 65536;
      amplified[i] = sample & 0xff;
      amplified[i + 1] = (sample >> 8) & 0xff;
    }
    return amplified;
  };

  const pcm16ToWav = (pcm16Bytes, sampleRate = 24000, channels = 1) => {
    const byteRate = sampleRate * channels * 2;
    const blockAlign = channels * 2;
    const subChunk2Size = pcm16Bytes.length;
    const chunkSize = 36 + subChunk2Size;

    const header = new ArrayBuffer(44);
    const view = new DataView(header);

    view.setUint32(0, 0x52494646, false); // "RIFF"
    view.setUint32(4, chunkSize, true);
    view.setUint32(8, 0x57415645, false); // "WAVE"
    view.setUint32(12, 0x666d7420, false); // "fmt "
    view.setUint32(16, 16, true);
    view.setUint16(20, 1, true); // Audio format (PCM)
    view.setUint16(22, channels, true);
    view.setUint32(24, sampleRate, true);
    view.setUint32(28, byteRate, true);
    view.setUint16(32, blockAlign, true);
    view.setUint16(34, 16, true); // 16 bits per sample
    view.setUint32(36, 0x64617461, false); // "data"
    view.setUint32(40, subChunk2Size, true);

    const wavFile = new Uint8Array(44 + pcm16Bytes.length);
    wavFile.set(new Uint8Array(header), 0);
    wavFile.set(pcm16Bytes, 44);

    return wavFile;
  };

  const audioBufferToPCM16FromFloat32 = (float32Array) => {
    const pcm16 = new Int16Array(float32Array.length);

    for (let i = 0; i < float32Array.length; i++) {
      const s = Math.max(-1, Math.min(1, float32Array[i]));
      pcm16[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
    }

    return new Uint8Array(pcm16.buffer);
  };

  // Fonction pour streamer l'audio en temps réel chunk par chunk
  const streamAudioChunk = useCallback(
    (pcmChunk) => {
      try {
        console.log(
          "🎵 streamAudioChunk called - chunk size:",
          pcmChunk.length
        );

        // Initialiser le contexte audio de lecture si nécessaire
        if (!playbackAudioContextRef.current) {
          playbackAudioContextRef.current = new AudioContext({
            sampleRate: SAMPLE_RATE,
          });
          playbackStartTimeRef.current =
            playbackAudioContextRef.current.currentTime;
          playbackScheduledTimeRef.current = 0;
          isStreamingAudioRef.current = true;
          console.log(
            "🔊 Streaming audio context initialized - PLAYBACK STARTED"
          );
        }

        const audioContext = playbackAudioContextRef.current;
        console.log(
          "📊 Audio context state:",
          audioContext.state,
          "- Current time:",
          audioContext.currentTime
        );

        // Convertir PCM16 bytes en Float32Array pour AudioBuffer
        const int16Array = new Int16Array(pcmChunk.buffer);
        const float32Array = new Float32Array(int16Array.length);

        for (let i = 0; i < int16Array.length; i++) {
          float32Array[i] =
            int16Array[i] / (int16Array[i] < 0 ? 0x8000 : 0x7fff);
        }

        // Créer un AudioBuffer
        const audioBuffer = audioContext.createBuffer(
          1,
          float32Array.length,
          SAMPLE_RATE
        );
        audioBuffer.getChannelData(0).set(float32Array);

        // Créer un source node et le connecter
        const sourceNode = audioContext.createBufferSource();
        sourceNode.buffer = audioBuffer;

        // Amplifier le son (facultatif, comme dans votre code original)
        const gainNode = audioContext.createGain();
        gainNode.gain.value = 2.5; // Amplification

        sourceNode.connect(gainNode);
        gainNode.connect(audioContext.destination);

        // Calculer quand jouer ce chunk pour éviter les gaps
        const scheduledTime =
          playbackStartTimeRef.current + playbackScheduledTimeRef.current;
        const currentTime = audioContext.currentTime;

        // Si on est en retard, rattraper le temps perdu
        const startTime = Math.max(scheduledTime, currentTime);

        console.log(
          "⏰ Scheduling chunk - startTime:",
          startTime,
          "duration:",
          audioBuffer.duration
        );

        // Callback quand ce chunk finit de jouer
        sourceNode.onended = () => {
          chunksPlayedRef.current++;
          console.log(
            "✅ Chunk",
            chunksPlayedRef.current,
            "/",
            totalChunksToPlayRef.current,
            "finished"
          );

          // Si c'était le dernier chunk, juste mettre à jour l'état
          if (
            chunksPlayedRef.current >= totalChunksToPlayRef.current &&
            totalChunksToPlayRef.current > 0
          ) {
            console.log("🏁 All chunks played");
            setIsPlaying(false);
            setIsAudioPaused(false);
          }
        };

        sourceNode.start(startTime);
        console.log("✅ Chunk started playing!");

        // Mettre à jour le temps schedulé
        const bufferDuration = audioBuffer.duration;
        playbackScheduledTimeRef.current += bufferDuration;

        // Garder une référence pour pouvoir arrêter si nécessaire
        playbackSourceNodeRef.current = sourceNode;

        // Réinitialiser le timeout de connexion lente
        if (streamingTimeoutRef.current) {
          clearTimeout(streamingTimeoutRef.current);
        }

        // Détecter si la connexion devient trop lente
        streamingTimeoutRef.current = setTimeout(() => {
          console.warn(
            "Slow connection detected - audio chunks not arriving fast enough"
          );
          // On pourrait afficher un indicateur de chargement ici
        }, STREAMING_TIMEOUT);
      } catch (e) {
        console.error("❌ Error streaming audio chunk:", e);
      }
    },
    [SAMPLE_RATE, STREAMING_TIMEOUT]
  );

  // Fonction pour arrêter le streaming et nettoyer
  const stopStreamingAudio = useCallback(() => {
    if (playbackAudioContextRef.current) {
      // Arrêter le source node actuel
      if (playbackSourceNodeRef.current) {
        try {
          playbackSourceNodeRef.current.stop();
        } catch (e) {
          // Ignore si déjà arrêté
        }
        playbackSourceNodeRef.current = null;
      }

      // Fermer le contexte audio
      playbackAudioContextRef.current.close();
      playbackAudioContextRef.current = null;
    }

    // Nettoyer les timeouts
    if (streamingTimeoutRef.current) {
      clearTimeout(streamingTimeoutRef.current);
      streamingTimeoutRef.current = null;
    }

    // Réinitialiser les états
    playbackStartTimeRef.current = 0;
    playbackScheduledTimeRef.current = 0;
    isStreamingAudioRef.current = false;
    streamingBufferRef.current = [];

    console.log("Streaming audio stopped and cleaned up");
  }, []);

  // Fonction pour mettre en pause l'audio (streaming uniquement)
  const pauseAudio = useCallback(() => {
    if (!isPlaying || isAudioPaused) return;

    if (
      playbackAudioContextRef.current &&
      playbackAudioContextRef.current.state === "running"
    ) {
      playbackAudioContextRef.current.suspend();
      setIsAudioPaused(true);
      console.log("⏸️ Audio paused");
    }
  }, [isPlaying, isAudioPaused]);

  // Fonction pour reprendre la lecture (streaming uniquement)
  const resumeAudio = useCallback(() => {
    if (!isPlaying || !isAudioPaused) return;

    if (
      playbackAudioContextRef.current &&
      playbackAudioContextRef.current.state === "suspended"
    ) {
      playbackAudioContextRef.current.resume();
      setIsAudioPaused(false);
      console.log("▶️ Audio resumed");
    }
  }, [isPlaying, isAudioPaused]);

  // Fonction unifiée pour pause/resume qui gère les deux systèmes (streaming et historique)
  const togglePlayPause = useCallback(() => {
    // Si on a un AudioContext actif (streaming)
    if (playbackAudioContextRef.current) {
      if (isAudioPaused) {
        resumeAudio();
      } else {
        pauseAudio();
      }
    }
    // Si on a un Audio element actif (historique)
    else if (currentAudioRef.current) {
      if (currentAudioRef.current.paused) {
        currentAudioRef.current.play();
        setIsAudioPaused(false);
      } else {
        currentAudioRef.current.pause();
        setIsAudioPaused(true);
      }
    }
  }, [isAudioPaused, pauseAudio, resumeAudio]);

  const startRecording = async () => {
    if (isRecording || isProcessing) return;

    // Si un audio est en pause, l'arrêter complètement avant d'enregistrer
    if (isPlaying && isAudioPaused) {
      console.log("🛑 Stopping paused audio before recording");

      // Arrêter l'AudioContext de streaming si actif
      if (playbackAudioContextRef.current) {
        stopStreamingAudio();
      }

      // Arrêter l'Audio element d'historique si actif
      if (currentAudioRef.current) {
        currentAudioRef.current.pause();
        currentAudioRef.current = null;
      }

      // Réinitialiser les états
      setIsPlaying(false);
      setPlayingIndex(null);
      setIsAudioPaused(false);
    }

    try {
      // Vérifier si on a déjà la permission
      let alreadyHadPermission = microphonePermissionGrantedRef.current;

      // Si on n'a pas encore vérifié, essayer de vérifier via l'API Permissions
      if (
        !alreadyHadPermission &&
        navigator.permissions &&
        navigator.permissions.query
      ) {
        try {
          const permissionStatus = await navigator.permissions.query({
            name: "microphone",
          });
          alreadyHadPermission = permissionStatus.state === "granted";
          if (alreadyHadPermission) {
            microphonePermissionGrantedRef.current = true;
          }
        } catch (e) {
          // L'API peut ne pas être supportée sur tous les navigateurs
          console.log("Permission API not supported");
        }
      }

      // Demander l'accès au microphone avec des paramètres de traitement audio optimisés
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          sampleRate: 24000,
          channelCount: 1,
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      });

      // Si on n'avait pas déjà la permission (vient d'être accordée), arrêter et informer l'utilisateur
      if (!alreadyHadPermission) {
        microphonePermissionGrantedRef.current = true;
        stream.getTracks().forEach((track) => track.stop());
        enqueueSnackbar(
          t("toasts.microphoneGranted") ||
            "Microphone access granted! Please press and hold again to record.",
          { variant: "success" }
        );
        return;
      }

      // Créer AudioContext pour capturer l'audio brut
      audioContextRef.current = new AudioContext({ sampleRate: 24000 });
      const source = audioContextRef.current.createMediaStreamSource(stream);

      // Créer un ScriptProcessorNode pour capturer les échantillons PCM
      const bufferSize = 4096;
      const processor = audioContextRef.current.createScriptProcessor(
        bufferSize,
        1,
        1
      );

      // Réinitialiser pour le nouvel enregistrement
      recordedAudioBlobRef.current = null;
      const recordedChunks = [];
      recordingStartTimeRef.current = Date.now();
      shouldSendChunksRef.current = true; // Autoriser l'envoi des chunks

      // Créer aussi un MediaRecorder pour sauvegarder dans l'historique
      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: "audio/webm;codecs=opus",
      });

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          recordedChunks.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        const completeBlob = new Blob(recordedChunks, { type: "audio/webm" });
        recordedAudioBlobRef.current = completeBlob;
      };

      // Traiter les échantillons audio en temps réel
      processor.onaudioprocess = (e) => {
        // Ne pas envoyer de chunks si on a arrêté l'envoi
        if (!shouldSendChunksRef.current) {
          return;
        }

        const inputData = e.inputBuffer.getChannelData(0);
        const pcm16 = audioBufferToPCM16FromFloat32(inputData);
        const base64Chunk = uint8ArrayToBase64(pcm16);

        webRealtimeSocket.emit("audio-chunk", { chunk: base64Chunk });
      };

      // Connecter les nœuds
      source.connect(processor);
      processor.connect(audioContextRef.current.destination);

      // Sauvegarder les références
      mediaRecorderRef.current = {
        mediaRecorder,
        processor,
        source,
        stream,
        stop: () => {
          processor.disconnect();
          source.disconnect();
          mediaRecorder.stop();
          stream.getTracks().forEach((track) => track.stop());
        },
      };

      mediaRecorder.start();
      setIsRecording(true);
    } catch (e) {
      console.error("Error starting recording:", e);
      enqueueSnackbar(t("toasts.microphoneError") || "Microphone error", {
        variant: "error",
      });
    }
  };

  const stopRecording = () => {
    if (!isRecording) return;

    // Vérifier la durée de l'enregistrement
    const recordingDuration = Date.now() - recordingStartTimeRef.current;
    const isTooShort = recordingDuration < MIN_RECORDING_DURATION;

    if (mediaRecorderRef.current) {
      // IMPORTANT: Arrêter IMMÉDIATEMENT l'envoi des chunks pour éviter d'envoyer du bruit après "end"
      shouldSendChunksRef.current = false;

      // Arrêter tout (processor, source, mediaRecorder, stream)
      mediaRecorderRef.current.stop();

      // Utiliser un court délai pour permettre à mediaRecorder.onstop de se déclencher
      // et finaliser le blob audio avant d'envoyer "end" au backend
      setTimeout(() => {
        if (recordedAudioBlobRef.current && !isTooShort) {
          // Ajouter l'audio envoyé aux answers seulement si assez long
          const userAudioMessage = {
            type: "audio",
            audioBlob: recordedAudioBlobRef.current,
            timestamp: Date.now(),
            isUser: true,
          };

          // Appliquer la transcription en attente si elle existe
          if (pendingTranscriptionRef.current) {
            console.log(
              "Applying pending transcription to user message:",
              pendingTranscriptionRef.current
            );
            userAudioMessage.transcription = pendingTranscriptionRef.current;
            pendingTranscriptionRef.current = null; // Nettoyer après utilisation
          }

          setAnswers([...tmpAnswers.current, userAudioMessage]);
          tmpAnswers.current = [...tmpAnswers.current, userAudioMessage];

          // Envoyer "end" au backend APRÈS avoir arrêté l'envoi des chunks
          // pour garantir que tous les chunks audio sont arrivés avant la fin
          setIsProcessing(true);
          webRealtimeSocket.emit("end", { configId, targetId, zoneId });
          console.log("Recording stopped, end event sent to backend");
        } else if (isTooShort) {
          console.log(
            `Recording too short: ${recordingDuration}ms, minimum is ${MIN_RECORDING_DURATION}ms`
          );
          enqueueSnackbar(
            t("toasts.recordingTooShort") ||
              "Recording too short, please hold longer",
            { variant: "warning" }
          );
        }
        mediaRecorderRef.current = null;
      }, 100); // Réduit à 100ms - juste assez pour que onstop se déclenche
    }

    if (audioContextRef.current) {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }

    setIsRecording(false);
    recordingStartTimeRef.current = null;
  };

  const handleMouseDown = () => {
    // Permettre l'enregistrement si pas en processing et (pas de lecture OU lecture en pause ET réception terminée)
    if (
      !isProcessing &&
      (!isPlaying || (isAudioPaused && !isReceivingAudioRef.current))
    ) {
      startRecording();
    }
  };

  const handleMouseUp = () => {
    if (isRecording) {
      stopRecording();
    }
  };

  const playAudioFromHistory = async (audioBlob, index) => {
    // Arrêter l'audio en cours si nécessaire
    if (currentAudioRef.current) {
      currentAudioRef.current.pause();
      currentAudioRef.current = null;
    }

    // Si on clique sur l'audio en cours de lecture, toggle pause/play
    if (playingIndex === index && isPlaying) {
      togglePlayPause();
      return;
    }

    try {
      const audioUrl = URL.createObjectURL(audioBlob);
      const audio = new Audio(audioUrl);
      audio.volume = 1.0;
      currentAudioRef.current = audio;

      setIsPlaying(true);
      setPlayingIndex(index);
      setIsAudioPaused(false);

      audio.onended = () => {
        console.log("Audio playback completed");
        URL.revokeObjectURL(audioUrl);
        setIsPlaying(false);
        setPlayingIndex(null);
        setIsAudioPaused(false);
      };

      audio.onerror = (e) => {
        console.error("Error playing audio:", e);
        URL.revokeObjectURL(audioUrl);
        setIsPlaying(false);
        setPlayingIndex(null);
        setIsAudioPaused(false);
      };

      await audio.play();
    } catch (e) {
      console.error("Error playing audio from history:", e);
      setIsPlaying(false);
      setPlayingIndex(null);
      setIsAudioPaused(false);
    }
  };

  const handleSendText = () => {
    setAnswers([...answers, message]);
    tmpAnswers.current = [...answers, message];
    setMessage("");
    setLoading(true);
    webChatSocket.emit("message", { configId, message, targetId, zoneId }, () =>
      enqueueSnackbar(t("toasts.socketTimeout"), { variant: "error" })
    );
  };

  const handleModeSelection = (mode) => {
    setInputMode(mode);
    setModeSelected(true);
  };

  const scrollToBottom = useCallback(() => {
    const element = document.getElementById(`answersContainer-${configId}`);
    if (element && open)
      element.scrollTop = element.scrollHeight - element.clientHeight;
  }, [configId, open]);

  // useEffect pour nettoyer le contexte audio quand la lecture se termine
  useEffect(() => {
    if (!isPlaying && playbackAudioContextRef.current) {
      console.log("🧹 Cleaning up audio context after playback");
      stopStreamingAudio();
    }
  }, [isPlaying, stopStreamingAudio]);

  useEffect(() => {
    if (answers?.length > 0) scrollToBottom();
  }, [answers, scrollToBottom]);

  const handleAnswers = (res) => {
    setAnswers([...tmpAnswers.current, res]);
  };

  // useEffect pour l'initialisation à l'ouverture du dialog
  useEffect(() => {
    if (open) {
      const initialAnswers = introductionAI ? [introductionAI] : [];
      setAnswers(initialAnswers);
      tmpAnswers.current = initialAnswers;
      setLoading(false);
      setMessage("");

      // Réinitialiser la sélection du mode si speechToSpeech est activé
      if (speechToSpeech) {
        setInputMode(null);
        setModeSelected(false);
      } else {
        // Si pas de speechToSpeech, on est en mode write par défaut
        setInputMode("write");
        setModeSelected(true);
      }
    }
  }, [open, introductionAI, speechToSpeech]);

  // useEffect séparé pour la gestion des WebSockets
  useEffect(() => {
    if (!open || !modeSelected) {
      return; // Ne rien faire si le dialog n'est pas ouvert ou si aucun mode n'est sélectionné
    }

    // Connexion exclusive : soit Realtime (audio), soit Chat (texte)
    if (speechToSpeech && inputMode === "talk") {
      // Mode Speech-to-Speech : utiliser webRealtimeSocket uniquement
      webRealtimeSocket.connect();

      webRealtimeSocket.on("connect", () => {
        console.log("WebRealtimeSocket connected:", webRealtimeSocket.id);
        webRealtimeSocket.emit("start", { configId, targetId, zoneId });
      });

      webRealtimeSocket.on("started", (data) => {
        console.log("Server started:", data);
      });

      webRealtimeSocket.on("openai-event", (data) => {
        try {
          // Gérer les erreurs
          if (data?.type === "error") {
            console.error("OpenAI Realtime error:", data);

            // Si c'est l'erreur "conversation_already_has_active_response",
            // on attend simplement que la réponse se termine
            if (
              data?.error?.code === "conversation_already_has_active_response"
            ) {
              console.log("Waiting for previous response to finish...");
              return; // Ne pas réinitialiser les états, juste attendre
            }

            // Pour les autres erreurs, réinitialiser
            setIsProcessing(false);
            setIsPlaying(false);
            audioBufferRef.current = [];
            audioSavedRef.current = false;

            const errorMessage =
              data?.error?.message || "An error occurred with the AI response";
            enqueueSnackbar(errorMessage, { variant: "error" });
            return;
          }

          if (data?.status === "failed") {
            console.error("Response failed:", data?.status_details);
            setIsProcessing(false);
            setIsPlaying(false);
            audioBufferRef.current = [];
            audioSavedRef.current = false;

            const errorMessage =
              data?.status_details?.error?.message || "Response failed";
            enqueueSnackbar(errorMessage, { variant: "error" });
            return;
          }

          if (data?.type === "response.audio.delta") {
            console.log("📥 response.audio.delta event received");

            if (!isReceivingAudioRef.current) {
              isReceivingAudioRef.current = true;
              audioBufferRef.current = [];
              audioSavedRef.current = false; // Réinitialiser pour le nouveau message
              streamingBufferRef.current = [];

              // Réinitialiser les états de contrôle audio
              setIsPlaying(false);
              setIsAudioPaused(false);

              console.log(
                "🎬 Started receiving audio response - buffers initialized"
              );
            }

            const deltaB64 = data.delta;
            if (deltaB64) {
              try {
                const pcmBytes = base64ToUint8Array(deltaB64);
                console.log("🔢 PCM bytes decoded - size:", pcmBytes.length);
                audioBufferRef.current.push(pcmBytes); // Garder pour sauvegarder plus tard

                // Si le streaming n'a pas encore commencé, buffer les chunks
                if (!isStreamingAudioRef.current) {
                  streamingBufferRef.current.push(pcmBytes);
                  console.log(
                    "📦 Buffering chunk -",
                    streamingBufferRef.current.length,
                    "/",
                    MIN_BUFFER_CHUNKS,
                    "chunks buffered"
                  );

                  // Commencer le streaming dès qu'on a assez de chunks (buffering initial)
                  if (streamingBufferRef.current.length >= MIN_BUFFER_CHUNKS) {
                    console.log(
                      `✅ Buffering complete (${MIN_BUFFER_CHUNKS} chunks), starting streaming NOW!`
                    );

                    // Initialiser les compteurs pour le total de chunks
                    totalChunksToPlayRef.current =
                      streamingBufferRef.current.length;
                    chunksPlayedRef.current = 0;

                    // Streamer automatiquement tous les chunks bufferisés
                    while (streamingBufferRef.current.length > 0) {
                      const chunk = streamingBufferRef.current.shift();
                      console.log("▶️ Streaming buffered chunk...");
                      streamAudioChunk(chunk);
                    }

                    // Mettre à jour les états UI
                    setIsProcessing(false);
                    setIsPlaying(true);
                    setIsAudioPaused(false);
                    setPlayingIndex(tmpAnswers.current.length); // Index du futur message

                    // Créer un message temporaire "en cours de lecture"
                    const streamingPlaceholder = {
                      type: "audio-streaming",
                      isUser: false,
                      timestamp: Date.now(),
                    };

                    // Ajouter le placeholder à l'affichage
                    setAnswers([...tmpAnswers.current, streamingPlaceholder]);
                    tmpAnswers.current = [
                      ...tmpAnswers.current,
                      streamingPlaceholder,
                    ];

                    console.log(
                      "🎭 UI updated - isPlaying=true, streaming placeholder added"
                    );
                  }
                } else {
                  // Le streaming a déjà commencé, streamer immédiatement chaque chunk
                  console.log(
                    "▶️ Streaming mode active - streaming chunk immediately"
                  );

                  // Incrémenter le total de chunks à jouer
                  totalChunksToPlayRef.current++;

                  streamAudioChunk(pcmBytes);
                }
              } catch (e) {
                console.error("Error decoding audio delta:", e);
              }
            }
          } else if (data?.type === "response.audio.done") {
            // Vérifier si la réponse a échoué
            if (data?.status === "failed") {
              console.error("Audio response failed:", data?.status_details);
              setIsProcessing(false);
              setIsPlaying(false);
              audioBufferRef.current = [];
              audioSavedRef.current = false;
              stopStreamingAudio();
              return;
            }

            // NE PAS streamer automatiquement les derniers chunks
            // Ils seront streamés quand l'utilisateur clique sur Play
            console.log(
              "📦 Audio reception done - chunks buffered:",
              streamingBufferRef.current.length
            );

            // Audio terminé, sauvegarder dans l'historique si pas déjà fait
            if (audioBufferRef.current.length > 0 && !audioSavedRef.current) {
              console.log("Audio response completed, saving to history");

              // Créer un blob pour l'historique (à partir de tous les chunks reçus)
              const totalLength = audioBufferRef.current.reduce(
                (sum, chunk) => sum + chunk.length,
                0
              );
              const combinedPcm = new Uint8Array(totalLength);
              let offset = 0;
              for (const chunk of audioBufferRef.current) {
                combinedPcm.set(chunk, offset);
                offset += chunk.length;
              }

              const amplifiedPcm = amplifyAudio(combinedPcm, 2.5);
              const wavBytes = pcm16ToWav(amplifiedPcm, SAMPLE_RATE, 1);
              const blob = new Blob([wavBytes], { type: "audio/wav" });

              const audioMessage = {
                type: "audio",
                audioBlob: blob,
                timestamp: Date.now(),
                isUser: false,
              };

              // Appliquer la transcription IA en attente si elle existe
              if (pendingAITranscriptionRef.current) {
                console.log(
                  "Applying pending AI transcription:",
                  pendingAITranscriptionRef.current
                );
                audioMessage.transcription = pendingAITranscriptionRef.current;
                pendingAITranscriptionRef.current = null;
              }

              // Remplacer le placeholder "audio-streaming" par le vrai message audio
              const updatedAnswers = tmpAnswers.current.map((msg) =>
                msg.type === "audio-streaming" ? audioMessage : msg
              );
              setAnswers(updatedAnswers);
              tmpAnswers.current = updatedAnswers;

              console.log(
                "🔄 Replaced streaming placeholder with final audio message"
              );
              audioSavedRef.current = true;

              // La réception est terminée
              isReceivingAudioRef.current = false;

              // IMPORTANT: On garde isPlaying à true et playingIndex actif
              // pour que l'audio continue de jouer jusqu'au bout
              // On met juste à jour le playingIndex pour pointer vers le bon message
              const finalMessageIndex = tmpAnswers.current.findIndex(
                (msg) => msg === audioMessage
              );
              if (finalMessageIndex >= 0) {
                setPlayingIndex(finalMessageIndex);
              }

              console.log("📻 Audio reception complete - playback continues");

              // Attendre la fin naturelle du streaming pour nettoyer
              // On utilise un timeout basé sur la durée estimée de l'audio restant
              const estimatedDuration =
                (playbackScheduledTimeRef.current -
                  (playbackAudioContextRef.current?.currentTime -
                    playbackStartTimeRef.current || 0)) *
                1000;

              setTimeout(() => {
                stopStreamingAudio();
                setIsPlaying(false);
                setPlayingIndex(null);
                setIsAudioPaused(false);
                setIsProcessing(false);
                audioBufferRef.current = [];
                audioSavedRef.current = false;

                // Activer le cooldown de sécurité
                console.log(
                  `Starting ${RESPONSE_COOLDOWN}ms cooldown to prevent conversation_already_has_active_response`
                );
                setIsResponseCooldown(true);

                if (cooldownTimerRef.current) {
                  clearTimeout(cooldownTimerRef.current);
                }

                cooldownTimerRef.current = setTimeout(() => {
                  setIsResponseCooldown(false);
                  cooldownTimerRef.current = null;
                }, RESPONSE_COOLDOWN);
              }, Math.max(0, estimatedDuration) + 100); // +100ms de marge
            } else {
              console.log("Audio done but buffer empty");
              setIsProcessing(false);
              stopStreamingAudio();
              audioSavedRef.current = false;
              isReceivingAudioRef.current = false;
            }
          } else if (data?.type === "response.done") {
            // Vérifier si la réponse a échoué
            if (data?.status === "failed") {
              console.error("Response failed:", data?.status_details);
              setIsProcessing(false);
              audioBufferRef.current = [];
              audioSavedRef.current = false;
              return;
            }

            // En mode audio, on ignore response.done et on attend response.audio.done
            // response.done peut arriver avant que les chunks audio ne commencent
            console.log("Response done event received, waiting for audio.done");
            // Ne rien faire - on laisse isProcessing actif jusqu'à response.audio.done
          }
        } catch (e) {
          console.error("Error processing openai-event:", e);
        }
      });

      webRealtimeSocket.on("answer-finished", (data) => {
        console.log("Answer finished", data);

        // Extraire la transcription de la réponse IA
        let aiTranscription = null;
        try {
          if (data?.detail?.response?.output?.[0]?.content?.[0]?.transcript) {
            aiTranscription =
              data.detail.response.output[0].content[0].transcript;
            console.log("AI transcription found:", aiTranscription);
          }
        } catch (e) {
          console.error("Error extracting AI transcription:", e);
        }

        // Appliquer la transcription IA si elle existe
        if (aiTranscription) {
          // Si le message audio existe déjà (streamé), y ajouter la transcription
          if (tmpAnswers.current.length > 0) {
            const lastMessage =
              tmpAnswers.current[tmpAnswers.current.length - 1];
            if (lastMessage?.type === "audio" && !lastMessage?.isUser) {
              console.log(
                "Adding AI transcription to existing streamed message"
              );
              lastMessage.transcription = aiTranscription;
              setAnswers([...tmpAnswers.current]);
            }
          } else {
            // Sinon, stocker pour plus tard
            pendingAITranscriptionRef.current = aiTranscription;
          }
        }

        console.log("Answer finished - audio was streamed in real-time");
      });

      webRealtimeSocket.on("transcription", (data) => {
        console.log("Received transcription:", data);
        console.log("Current tmpAnswers:", tmpAnswers.current);

        // Chercher le dernier message audio UTILISATEUR (pas l'IA)
        let userAudioIndex = -1;
        for (let i = tmpAnswers.current.length - 1; i >= 0; i--) {
          const message = tmpAnswers.current[i];
          if (message?.type === "audio" && message?.isUser === true) {
            userAudioIndex = i;
            break;
          }
        }

        console.log("User audio message index:", userAudioIndex);

        if (userAudioIndex >= 0) {
          const transcriptionText = data.text || data.transcript || data;
          console.log("Setting transcription:", transcriptionText);
          tmpAnswers.current[userAudioIndex].transcription = transcriptionText;
          setAnswers([...tmpAnswers.current]);
          console.log("Transcription set successfully");
        } else {
          // Si pas encore de message utilisateur audio, stocker la transcription temporairement
          console.log(
            "No user audio message found, storing transcription for later"
          );
          pendingTranscriptionRef.current =
            data.text || data.transcript || data;
        }
      });

      webRealtimeSocket.on("disconnect", () => {});
    } else if (modeSelected) {
      // Mode Chat (texte) : utiliser webChatSocket uniquement
      webChatSocket.connect();
      webChatSocket.on("answer", (res) => {
        handleAnswers(res);
        setLoading(false);
      });
    }

    return () => {
      // Cleanup selon le mode
      if (speechToSpeech && inputMode === "talk") {
        // Nettoyage mode Realtime
        webRealtimeSocket.off("connect");
        webRealtimeSocket.off("started");
        webRealtimeSocket.off("openai-event");
        webRealtimeSocket.off("answer-finished");
        webRealtimeSocket.off("transcription");
        webRealtimeSocket.off("disconnect");
        webRealtimeSocket.disconnect();

        // Clean up audio resources
        if (mediaRecorderRef.current) {
          mediaRecorderRef.current.stop();
          mediaRecorderRef.current.stream
            .getTracks()
            .forEach((track) => track.stop());
        }
        if (audioContextRef.current) {
          audioContextRef.current.close();
        }
        if (currentAudioRef.current) {
          currentAudioRef.current.pause();
        }

        // Nettoyer le streaming audio
        stopStreamingAudio();

        audioBufferRef.current = [];

        // Nettoyer le timer de cooldown
        if (cooldownTimerRef.current) {
          clearTimeout(cooldownTimerRef.current);
          cooldownTimerRef.current = null;
        }
      } else {
        // Nettoyage mode Chat
        webChatSocket.off("answer");
        webChatSocket.disconnect();
      }
    };
  }, [
    open,
    modeSelected,
    speechToSpeech,
    inputMode,
    configId,
    targetId,
    zoneId,
    enqueueSnackbar,
    streamAudioChunk,
    stopStreamingAudio,
    MIN_BUFFER_CHUNKS,
    RESPONSE_COOLDOWN,
  ]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      disableEscapeKeyDown
      aria-labelledby="ai-dialog-webxr"
      aria-describedby="ai-dialog-webxr-desc"
    >
      <AIDialogContainer style={{ overflow: "hidden" }}>
        <DialogTitle sx={{ paddingBottom: "0" }}>
          {t("player.buttons.letstalk")}
        </DialogTitle>
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={(theme) => ({
            position: "absolute",
            right: 10,
            top: 10,
            color: theme.palette.grey[500],
          })}
        >
          <Close />
        </IconButton>
        <DialogContent sx={{ overflow: "unset", minWidth: "250px" }}>
          {speechToSpeech && !modeSelected ? (
            // Afficher seulement les boutons de sélection de mode
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: "16px",
                padding: "32px 16px",
                justifyContent: "center",
                alignItems: "center",
                minHeight: "200px",
              }}
            >
              <Button
                variant="outlined"
                startIcon={<Chat />}
                onClick={() => handleModeSelection("write")}
                sx={{
                  width: "100%",
                  maxWidth: "300px",
                  padding: "12px 24px",
                  textTransform: "none",
                  fontSize: "16px",
                }}
              >
                {t("player.buttons.writeMode") || "Écrire"}
              </Button>
              <Button
                variant="outlined"
                startIcon={<RecordVoiceOver />}
                onClick={() => handleModeSelection("talk")}
                sx={{
                  width: "100%",
                  maxWidth: "300px",
                  padding: "12px 24px",
                  textTransform: "none",
                  fontSize: "16px",
                }}
              >
                {t("player.buttons.talkMode") || "Discuter"}
              </Button>
            </Box>
          ) : (
            // Afficher l'interface normale
            <Box className={classes.messagesContainer}>
              <Paper
                elevation={5}
                className={classes.AIResponseContainer}
                id={`answersContainer-${configId}`}
              >
                {answers?.map((item, index) => {
                  const isAudio =
                    typeof item === "object" && item.type === "audio";
                  const isAudioStreaming =
                    typeof item === "object" && item.type === "audio-streaming";
                  // Pour les messages audio, utiliser item.isUser, sinon utiliser l'index
                  const isUserMessage = isAudio
                    ? item.isUser === true
                    : isAudioStreaming
                    ? false
                    : index % 2 === 1;

                  return (
                    <Box
                      key={`answer-${index + 1}-${configId}`}
                      sx={{ display: "flex" }}
                    >
                      {isUserMessage && <Box sx={{ width: "45%" }} />}
                      <Paper
                        elevation={12}
                        sx={{
                          width: "200px",
                          padding: "0.5rem",
                          marginBottom: "0.75rem",
                          cursor: isAudio ? "pointer" : "default",
                          backgroundColor: isUserMessage
                            ? isAudio && playingIndex === index
                              ? "#3d3636"
                              : "#4f4848"
                            : (isAudio && playingIndex === index) ||
                              isAudioStreaming
                            ? "rgba(255, 255, 255, 0.3)"
                            : "#f0f0f0",
                          transition: "background-color 0.3s ease",
                          "&:hover": isAudio
                            ? {
                                backgroundColor: isUserMessage
                                  ? "#3d3636"
                                  : "rgba(255, 255, 255, 0.2)",
                              }
                            : {},
                        }}
                        onClick={() =>
                          isAudio && playAudioFromHistory(item.audioBlob, index)
                        }
                      >
                        {isAudioStreaming ? (
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              gap: "8px",
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: "8px",
                                color: "#000",
                              }}
                            >
                              {/* IconButton pause/resume cliquable */}
                              <IconButton
                                onClick={(e) => {
                                  e.stopPropagation();
                                  e.preventDefault();
                                  togglePlayPause();
                                }}
                                size="small"
                                sx={{ padding: "4px" }}
                              >
                                {isAudioPaused ? (
                                  <PlayArrow
                                    sx={{ fontSize: "24px", color: "#000" }}
                                  />
                                ) : (
                                  <Pause
                                    sx={{ fontSize: "24px", color: "#000" }}
                                  />
                                )}
                              </IconButton>
                              <VolumeUp sx={{ fontSize: "20px" }} />
                              <span
                                style={{
                                  fontSize: "14px",
                                  fontFamily: "Lato, sans-serif",
                                }}
                              >
                                {isAudioPaused
                                  ? t("player.buttons.paused") || "En pause..."
                                  : t("player.buttons.playing") ||
                                    "Lecture en cours..."}
                              </span>
                            </Box>

                            {/* Message temporaire au lieu de la transcription */}
                            <Box
                              sx={{
                                fontSize: "12px",
                                fontFamily: "Lato, sans-serif",
                                color: "rgba(0,0,0,0.6)",
                                fontStyle: "italic",
                                marginTop: "8px",
                                paddingTop: "8px",
                                borderTop: "1px solid rgba(0,0,0,0.1)",
                                textAlign: "center",
                              }}
                            >
                              {t("player.buttons.receivingResponse") ||
                                "Réception de la réponse..."}
                            </Box>
                          </Box>
                        ) : isAudio ? (
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              gap: "8px",
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: "8px",
                                color: isUserMessage ? "#fff" : "#000",
                              }}
                            >
                              {/* IconButton cliquable pour pause/resume quand l'audio joue */}
                              {playingIndex === index && isPlaying ? (
                                <IconButton
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    e.preventDefault();
                                    togglePlayPause();
                                  }}
                                  size="small"
                                  sx={{ padding: "4px" }}
                                >
                                  {isAudioPaused ? (
                                    <PlayArrow
                                      sx={{
                                        fontSize: "24px",
                                        color: isUserMessage ? "#fff" : "#000",
                                      }}
                                    />
                                  ) : (
                                    <Pause
                                      sx={{
                                        fontSize: "24px",
                                        color: isUserMessage ? "#fff" : "#000",
                                      }}
                                    />
                                  )}
                                </IconButton>
                              ) : (
                                // Icône simple non cliquable pour les autres cas
                                <PlayArrow
                                  sx={{
                                    fontSize: "24px",
                                    color: isUserMessage ? "#fff" : "#000",
                                  }}
                                />
                              )}
                              <VolumeUp sx={{ fontSize: "20px" }} />
                              <span
                                style={{
                                  fontSize: "14px",
                                  fontFamily: "Lato, sans-serif",
                                }}
                              >
                                {playingIndex === index && isPlaying
                                  ? isAudioPaused
                                    ? t("player.buttons.paused") ||
                                      "En pause..."
                                    : t("player.buttons.playing") ||
                                      "Lecture en cours..."
                                  : t("player.buttons.audioMessage") ||
                                    "Message audio"}
                              </span>
                            </Box>

                            {item.transcription && (
                              <Box
                                sx={{
                                  fontSize: "12px",
                                  fontFamily: "Lato, sans-serif",
                                  color: isUserMessage
                                    ? "rgba(255,255,255,0.8)"
                                    : "rgba(0,0,0,0.7)",
                                  fontStyle: "italic",
                                  marginTop: "4px",
                                  paddingTop: "4px",
                                  borderTop: `1px solid ${
                                    isUserMessage
                                      ? "rgba(255,255,255,0.2)"
                                      : "rgba(0,0,0,0.1)"
                                  }`,
                                  lineHeight: 1.3,
                                  wordBreak: "break-word",
                                }}
                              >
                                {item.transcription.length >
                                  MAX_TRANSCRIPTION_LENGTH &&
                                !expandedTranscriptions.has(index) ? (
                                  <>
                                    "{truncateTranscription(item.transcription)}
                                    "
                                    <Box
                                      component="span"
                                      onClick={(e) => {
                                        e.stopPropagation(); // Empêcher la lecture audio
                                        toggleTranscriptionExpansion(index);
                                      }}
                                      sx={{
                                        color: isUserMessage
                                          ? "rgba(255,255,255,0.6)"
                                          : "rgba(0,0,0,0.5)",
                                        cursor: "pointer",
                                        textDecoration: "underline",
                                        marginLeft: "8px",
                                        "&:hover": {
                                          color: isUserMessage
                                            ? "rgba(255,255,255,0.9)"
                                            : "rgba(0,0,0,0.8)",
                                        },
                                      }}
                                    >
                                      afficher plus
                                    </Box>
                                  </>
                                ) : (
                                  <>
                                    "{item.transcription}"
                                    {item.transcription.length >
                                      MAX_TRANSCRIPTION_LENGTH && (
                                      <Box
                                        component="span"
                                        onClick={(e) => {
                                          e.stopPropagation(); // Empêcher la lecture audio
                                          toggleTranscriptionExpansion(index);
                                        }}
                                        sx={{
                                          color: isUserMessage
                                            ? "rgba(255,255,255,0.6)"
                                            : "rgba(0,0,0,0.5)",
                                          cursor: "pointer",
                                          textDecoration: "underline",
                                          marginLeft: "8px",
                                          "&:hover": {
                                            color: isUserMessage
                                              ? "rgba(255,255,255,0.9)"
                                              : "rgba(0,0,0,0.8)",
                                          },
                                        }}
                                      >
                                        afficher moins
                                      </Box>
                                    )}
                                  </>
                                )}
                              </Box>
                            )}
                          </Box>
                        ) : (
                          <TypeAnimation
                            splitter={(str) => str.split(/(?= )/)} // split by words
                            sequence={[item, () => scrollToBottom()]}
                            wrapper="p"
                            key={item}
                            speed={{ type: "keyStrokeDelayInMs", value: 10 }}
                            omitDeletionAnimation={true}
                            style={{
                              fontSize: "16px",
                              display: "block",
                              fontFamily: "Lato, sans-serif",
                              color: isUserMessage ? "#fff" : "#000",
                              margin: 0,
                              whiteSpace: "pre-line",
                            }}
                            cursor={false}
                            repeat={0}
                          >
                            {item}
                          </TypeAnimation>
                        )}
                      </Paper>
                      {!isUserMessage && <Box sx={{ width: "45%" }} />}
                    </Box>
                  );
                })}
              </Paper>
              {(!speechToSpeech || inputMode === "write") && (
                <Box className={classes.userTextContainer}>
                  <TextField
                    sx={{ flex: 1, minWidth: 0 }}
                    value={message}
                    multiline
                    rows={2}
                    placeholder={t("player.buttons.enterAIMessage")}
                    onChange={(e) => setMessage(e.target.value)}
                  />
                  {isMobile && (
                    <IconButton
                      color="primary"
                      onClick={handleSendText}
                      disabled={message.length <= 0 || loading}
                      aria-label={t("player.buttons.sendButton")}
                      sx={{ flexShrink: 0, mb: 0.5 }}
                    >
                      {loading ? (
                        <CircularProgress size={24} />
                      ) : (
                        <SendIcon />
                      )}
                    </IconButton>
                  )}
                </Box>
              )}
              {speechToSpeech && inputMode === "talk" && (
                <Box className={classes.userTextContainer}>
                  <IconButton
                    onMouseDown={handleMouseDown}
                    onMouseUp={handleMouseUp}
                    onMouseLeave={handleMouseUp}
                    onTouchStart={handleMouseDown}
                    onTouchEnd={handleMouseUp}
                    disabled={
                      isProcessing ||
                      (isPlaying && !isAudioPaused) ||
                      (isAudioPaused && isReceivingAudioRef.current) ||
                      isResponseCooldown
                    }
                    sx={{
                      backgroundColor: isRecording
                        ? "error.main"
                        : isProcessing
                        ? "primary.main" // Garder la couleur primaire pendant processing
                        : isPlaying && !isAudioPaused
                        ? "rgba(255, 0, 0, 0.3)"
                        : "primary.main",
                      color: "white",
                      boxShadow: isRecording
                        ? "0 0 15px 3px rgba(255, 0, 0, 0.4)"
                        : "none",
                      transition: "all 0.3s ease",
                      width: "56px",
                      height: "56px",
                      margin: "0 auto",
                      "&:hover": {
                        backgroundColor: isRecording
                          ? "error.dark"
                          : "primary.dark",
                      },
                      "&:disabled": {
                        backgroundColor:
                          isProcessing || isResponseCooldown
                            ? "primary.main" // Garder la couleur primaire visible pendant processing et cooldown
                            : "rgba(128, 128, 128, 0.3)",
                        color: "white",
                        cursor: "wait", // Curseur d'attente
                        opacity: isResponseCooldown ? 0.7 : 1, // Légèrement transparent pendant cooldown
                      },
                    }}
                  >
                    {isProcessing ? (
                      <CircularProgress size={24} sx={{ color: "white" }} />
                    ) : isResponseCooldown ? (
                      <CircularProgress
                        size={24}
                        sx={{ color: "white", opacity: 0.5 }}
                      />
                    ) : (
                      <Mic />
                    )}
                  </IconButton>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          {!isMobile &&
            (!speechToSpeech || modeSelected) &&
            (inputMode === "write" || !speechToSpeech) && (
              <Button
                onClick={handleSendText}
                disabled={message.length <= 0 || loading}
                variant="contained"
                color="primary"
              >
                {loading ? (
                  <CircularProgress size={28} />
                ) : (
                  t("player.buttons.sendButton")
                )}
              </Button>
            )}
        </DialogActions>
      </AIDialogContainer>
    </Dialog>
  );
};

export default AIDialog;
